import React, { useEffect, useMemo, useState } from "react";
import Error404 from "./Error404";
import Loader from "../components/Loader";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import brandsData from "../data/brands.json";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductCard from "../components/ProductCard";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts, fetchCategoriesForCollectionHandle } from "../lib/medusa";
import Modal from "react-bootstrap/Modal";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper";
import "swiper/css";
import "swiper/css/free-mode";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd, itemListJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";
import {
  categoryMatchesSelection,
  groupCategoriesForDisplay,
} from "../utils/categoryGroups";

export default function Products(props) {
  const { slug } = useParams();
  if (brandsData[slug]?.hidden) {
    return <><Helmet><meta name="robots" content="noindex, nofollow" /></Helmet><Error404 /></>;
  }
  return <PublishedProducts {...props} />;
}

function PublishedProducts({ setHeader }) {
  const { slug } = useParams();

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get("sort");


  const limit = 12;
  const [offset, setOffset] = useState(0);
  const latestOffsetRef = React.useRef(0);
  const nextBatchStartIndexRef = React.useRef(null);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState([]);
  const [brandCategories, setBrandCategories] = useState([]);

  useEffect(() => {
    latestOffsetRef.current = offset;
  }, [offset]);
  const swiperRef = React.useRef(null);
  const [currentBrand, setCurrentBrand] = useState(brandsData[slug]);
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = currentBrand
    ? `${t(currentBrand.title)} - ${SITE_NAME}`
    : SITE_NAME;
  const pageDescription = t("header.products.metaDescription");
  const ogImage = currentBrand?.heroImg
    ? currentBrand.heroImg.startsWith("http")
      ? currentBrand.heroImg
      : `${SITE_URL}${currentBrand.heroImg}`
    : DEFAULT_OG_IMAGE;
  const [showFilters, setShowFilters] = useState(false);

  const { isLoading, isFetching, isError } = useQuery({
    queryKey: ["products", slug, sort, limit, offset, selectedCategorySlugs, brandCategories],
    queryFn: () => {
      // Map slugs to IDs
      const ids = brandCategories
        .filter((c) => categoryMatchesSelection(c, selectedCategorySlugs, t))
        .map((c) => c.id);
      return fetchProducts(slug, sort, limit, offset, ids);
    },
    onSuccess: (data) => {
      if (latestOffsetRef.current !== offset) {
        // Prevent stale asynchronous responses from overriding current page state
        return;
      }
      if (data?.length) {
        const nextProducts = data[0];
        const nextCount = data[1];
        setProducts((prev) => {
          if (offset === 0) return nextProducts;

          const prevIds = new Set(prev.map((p) => p.id));
          const uniqueNext = nextProducts.filter((p) => !prevIds.has(p.id));
          return [...prev, ...uniqueNext];
        });
        setCount(nextCount);

        if (offset > 0 && nextBatchStartIndexRef.current !== null) {
          // Delay the scroll so React can render the newly-added items.
          setTimeout(() => {
            const nextItemIndex = nextBatchStartIndexRef.current + 1;
            const el = document.querySelector(`.product-wrapper li:nth-child(${nextItemIndex})`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            nextBatchStartIndexRef.current = null;
          }, 80);
        }

        window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        window.dataLayer.push({
          event: "view_item_list",
          ecommerce: {
            item_list_id: "related_products",
            item_list_name: "Related products",
            items: data[0].map((product, index) => ({
              item_id: product.id,
              item_name: product.title,
              affiliation: "DURO",
              index: index,
              item_brand: product?.collection?.title,
              item_category: product?.categories?.[0]?.name,
              item_list_id: "related_products",
              item_list_name: "Related products",
              item_variant: product?.variants?.[0]?.title,
              location_id: "",
              price: product.variants?.[0]?.prices?.[0]?.amount,
              quantity: product.variants?.[0]?.inventory_quantity,
            })),
          },
        });
      }
    }
  });

  useEffect(() => {
    (async () => {
      const cats = await fetchCategoriesForCollectionHandle(slug);
      setBrandCategories(cats || []);
    })();
  }, [slug]);

  const isLastPage = offset + limit >= count;
  const hasMore = count > 0 && !isLastPage;

  const handleNextPage = () => {
    if (offset + limit < count) {
      nextBatchStartIndexRef.current = products.length;
      setOffset((prevOffset) => prevOffset + limit);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const bottomOffset = 400;
      const reachedNearBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - bottomOffset;

      setIsOverlayVisible(!reachedNearBottom);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Ensure each brand page begins at top
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    setCurrentBrand(brandsData[slug]);
    setOffset(0);
    setProducts([]);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  const sortBy = (input) => {
    const params = new URLSearchParams(searchParams.toString());

    if (params.get("sort") === input) {
      params.delete("sort");
    } else {
      params.set("sort", input);
    }
    setSearchParams(params);
    setOffset(0);
    setProducts([]);
  };

  const itemsLabel = (n) => {
    if (i18n.language === 'me') {
      const lastTwo = n % 100;
      const last = n % 10;
      const word = (last === 1 && lastTwo !== 11) ? 'proizvod' : 'proizvoda';
      return `${n} ${word}`;
    }
    return `${n} ${t('items', { defaultValue: 'items' })}`;
  };
  const categoryOptions = useMemo(
    () => groupCategoriesForDisplay(brandCategories, t),
    [brandCategories, t]
  );

  const toggleCategory = (slug) => {
    setSelectedCategorySlugs((prev) => {
      const next = prev.length === 1 && prev[0] === slug ? [] : [slug];
      setOffset(0);
      setProducts([]);
      return next;
    });
  };

  const clearCategorySelection = () => {
    setSelectedCategorySlugs([]);
    setOffset(0);
    setProducts([]);
  };

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang === "me" ? "sr-ME" : "en" }}>
        {/* <!-- meta tag --> */}
        <meta charSet="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        {/* <!-- Meta Tags--> */}
        <meta
          name="description"
          content={pageDescription}
        />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <Helmet>
        <link
          rel="canonical"
          href={`https://parfimerija-duro.me/${i18n.language}/${i18n.language === 'me' ? `brendovi/${slug}/proizvodi` : `brands/${slug}/products`}`}
        />
        <link rel="alternate" href={`https://parfimerija-duro.me/en/brands/${slug}/products`} hrefLang="en" />
        <link rel="alternate" href={`https://parfimerija-duro.me/me/brendovi/${slug}/proizvodi`} hrefLang="sr-ME" />
        <link rel="alternate" href={`https://parfimerija-duro.me/me/brendovi/${slug}/proizvodi`} hrefLang="x-default" />
      </Helmet>
      <Loader />
      {/* // <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div
          className="rs-breadcrumbs"
          style={{
            backgroundImage: `url(${
              !currentBrand?.heroImg
                ? "/assets/images/breadcrumbs/inr-2.jpg"
                : currentBrand.heroImg
            })`,
          }}
        >
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {currentBrand?.title || slug}
                {/*<span className="watermark">{currentBrand.title}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs />
        {/* <!-- Breadcrumbs End --> */}

        <div className="rs-team style7 lion-bg height-ctrl md-pb-30 md-pt-30">
          {/* <!-- Overlay Shop Start --> */}
          <div
            className="container container-fixwidth"
            style={{ display: isOverlayVisible ? "block" : "none" }}
          >
            <div className="collection-overlay gray-bg4">
              <p className="collection-overlay-text">
              {count} {t("brands.collectionProducts")}
              </p>
            </div>
          </div>
          {/* <!-- Overlay Shop End --> */}
        </div>

        {/* <!-- Products Section Start --> */}
        {isLoading && <div className="loading-text lion-bg">Loading...</div>}
        {isError && (
          <div className="loading-text lion-bg">
            {t("errors.products", { defaultValue: "Proizvodi trenutno nisu dostupni. Pokušajte ponovo." })}
          </div>
        )}
        {!isLoading && !isError && products.length === 0 && (
          <div className="loading-text lion-bg">
            {t("products.noResults", { defaultValue: "Nema proizvoda za izabrane filtere." })}
          </div>
        )}
        {!isLoading && !isError && products?.length > 0 ? (
          <div className="rs-team style7 lion-bg height-ctrl md-pb-20 md-pt-20 product-load">
            <div className="container">
              <div className="row">
                {/* <!-- Filter Section --> */}
                {/* full-width with top categories rail */}
                <div className="col-12 products-section">
                  <div className="sec-title mb-10 mt-10 md-pb-10 md-pt-10">
                    <h2 className="title pb-10">{t("products.sub-title")}</h2>
                    <div className="heading-border-line left-style"></div>
                  </div>
                  {categoryOptions.length > 0 && (
                    <div className="category-rail mb-3 position-relative">
                      <Swiper
                        modules={[FreeMode]}
                        freeMode
                        slidesPerView="auto"
                        spaceBetween={10}
                        className="category-swiper"
                        onBeforeInit={(sw) => { swiperRef.current = sw; }}
                      >
                        {/* View all */}
                        <SwiperSlide key="view-all">
                          <button className={`category-link ${selectedCategorySlugs.length === 0 ? 'active' : ''}`} onClick={clearCategorySelection}>{t('viewAll', { defaultValue: 'Svi proizvodi' })}</button>
                        </SwiperSlide>
                        {categoryOptions.map((option) => {
                          const { slug, label } = option;
                          const active = selectedCategorySlugs.includes(slug);
                          return (
                            <SwiperSlide key={slug}>
                              <button className={`category-link ${active ? 'active' : ''}`} onClick={()=>toggleCategory(slug)}>{label}</button>
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>
                    </div>
                  )}
                  {/* count + filter bar (match All Products) */}
                  <div className="d-flex justify-content-between align-items-center mb-2 border-top pt-2">
                    <div className="items-count">{itemsLabel(count)}</div>
                    <div className="ms-auto d-flex align-items-center gap-3">
                      {(selectedCategorySlugs.length > 0) && (
                        <button type="button" className="clear-link" onClick={clearCategorySelection}>{t('actions.clear', { defaultValue: 'Clear' })}</button>
                      )}
                      <button className="pagination-btn" onClick={() => setShowFilters(true)}>
                        {t("actions.filters", { defaultValue: "Filteri" })}
                      </button>
                    </div>
                  </div>
                  {(selectedCategorySlugs.length > 0) && (
                    <div className="mb-3 d-flex align-items-center gap-3">
                      {categoryOptions
                        .filter((option) => selectedCategorySlugs.includes(option.slug))
                        .map((option) => (
                          <span key={option.slug} className="filter-chip">
                            {option.label}
                            <button onClick={clearCategorySelection}>×</button>
                          </span>
                        ))}
                    </div>
                  )}

                  {/* Brand Filters Modal */}
                  <Modal show={showFilters} onHide={() => setShowFilters(false)} centered scrollable>
                    <Modal.Header closeButton>
                      <Modal.Title>{t("filters.title", { defaultValue: "Filteri" })}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{background:'#a59278', color:'#fff'}}>
                      <div className="filter-section">
                        <span className="sort-label">{t("products.sortBy")}</span>
                        <ul className="sort-options">
                          <li><button className={`filter-btn ${searchParams.get("sort") === "popular" && "active"}`} onClick={() => sortBy("popular")}>{t("products.options.popular")}</button></li>
                          <li><button className={`filter-btn ${searchParams.get("sort") === "latest" && "active"}`} onClick={() => sortBy("latest")}>{t("products.options.latest_arrivals")}</button></li>
                          <li><button className={`filter-btn ${searchParams.get("sort") === "price_low" && "active"}`} onClick={() => sortBy("price_low")}>{t("products.options.price_low_high")}</button></li>
                          <li><button className={`filter-btn ${searchParams.get("sort") === "price_high" && "active"}`} onClick={() => sortBy("price_high")}>{t("products.options.price_high_low")}</button></li>
                        </ul>
                        <hr />
                        <span className="sort-label">{t("filters.categories", { defaultValue: "Kategorije" })}</span>
                        <ul className="sort-options" style={{maxHeight:'240px', overflowY:'auto'}}>
                          {categoryOptions.map((option) => {
                            const checked = selectedCategorySlugs.includes(option.slug);
                            return (
                              <li key={option.slug}>
                                <label className="d-flex align-items-center" style={{gap:'8px'}}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleCategory(option.slug)} />
                                  <span>{option.label}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <button className="pagination-btn" onClick={() => { setSelectedCategorySlugs([]); setShowFilters(false); }}>{t('actions.clear', { defaultValue: 'Clear' })}</button>
                      <button className="pagination-btn" onClick={() => setShowFilters(false)}>{t('actions.apply', { defaultValue: 'Apply' })}</button>
                    </Modal.Footer>
                  </Modal>
                  <ul className="product-wrapper">
                    {products.map((product, i) => (
                      <li className="col-md-4 col-6 product-item" key={i}>
                        <ProductCard product={product} />
                      </li>
                    ))}
                  </ul>
                  {/* SEO: Breadcrumb + ItemList JSON-LD */}
                  <StructuredData
                    data={[
                      breadcrumbJsonLd([
                        { name: i18n.language === 'me' ? 'Početna' : 'Home', item: `https://parfimerija-duro.me/${i18n.language}/` },
                        { name: i18n.language === 'me' ? 'Brendovi' : 'Brands', item: `https://parfimerija-duro.me/${i18n.language}/${i18n.language === 'me' ? 'brendovi' : 'brands'}` },
                        { name: currentBrand?.title || slug, item: `https://parfimerija-duro.me/${i18n.language}/${i18n.language === 'me' ? 'brendovi' : 'brands'}/${slug}` },
                        { name: i18n.language === 'me' ? 'Proizvodi' : 'Products', item: window.location.href },
                      ]),
                      itemListJsonLd(
                        (products || []).map((p) => ({
                          name: p.title,
                          url: `https://shop.parfimerija-duro.me/${i18n.language}/products/${p.handle}`,
                          image: p.thumbnail || p.images?.[0]?.url,
                          brand: p.collection?.title,
                          category: p.categories?.[0]?.name,
                          price: (p?.variants?.[0]?.prices?.[0]?.amount || 0) / 100,
                          currency: p?.variants?.[0]?.prices?.[0]?.currency_code?.toUpperCase() || 'EUR',
                          inStock: (p?.variants?.[0]?.inventory_quantity ?? 1) > 0,
                        }))
                      ),
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {hasMore ? (
          <div className="pagination lion-bg">
            <div className="container gap-4 flex-row justify-content-center">
              <button
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={isFetching}
                aria-disabled={isFetching}
              >
                {isFetching
                  ? t("actions.loading", { defaultValue: "Loading..." })
                  : t("products.loadMore", { defaultValue: "Load additional products" })}
              </button>
            </div>
          </div>
        ) : null}

        {/* <!-- Products Section End --> */}

        {/* <!-- Newsletter section start --> */}
        <div className="rs-newsletter lion-bg style1 pt-90 md-pt-60 mb--65">
          <Newsletter />
        </div>

        {/* <!-- Newsletter section end --> */}
      </div>
      {/* // <!-- Main content End --> */}
    </>
  );
}
